import { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import VerifyByEmail from "./verify-by-email";
import GreatJob from "./great-job";
import TwoStepVerification from "./Two-step-verification";
import VerifyByPhone from "./verify-by-phone";
import { getTenantGroupId } from "../../utils/general-config";
import { initiateVerification, signInUser, verifyMfa, verifyOtp } from "../../pages/verify-user/actions";
import { readCookie } from "../../utils/helpers";
import { currentDomain } from "../../utils/general-config";
import { DomainEnum } from "../../services/enum/environment-enum";
const STEPS = {
  GREAT_JOB: "greatJob",
  VERIFY_EMAIL: "verifyEmail",
  TWO_STEP_SELECT: "twoStepSelect",
  VERIFY_PHONE: "verifyPhone",
  COMPLETE: "complete", // Used to show the final view within TwoStepVerification
};
export default function PsscVerificationModal({
  psscVerificationModal,
  emailId,
  pwd,
  otpTarget,
  toSetOtpTarget,
  userGuid,
  callBackSecureSimpliHandler,
  togglePsscVerificationModal,
}) {
  const [isLoading, setIsLoading] = useState(false);
  // Use a single step state for clarity, starting with 'greatJob'
  const isSignSimpli = currentDomain() === DomainEnum.SIGNSIMPLI;
  const [currentStep, setCurrentStep] = useState(STEPS.GREAT_JOB);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [message, setMessage] = useState("");
  const [otp, setOtp] = useState(Array(6).fill(""));
  const toSetOtp = (o) => setOtp(o);
  const [multiFactorLevel, setMultiFactorLevel] = useState(2);
  // Function to move from GreatJob to VerifyEmail
  const handleInitiateContactVerification = async (contactType) => {
    setIsLoading(true);
    try {
      const payload = {
        UserGuid: userGuid || readCookie("userguid"),
        ContactType: contactType === "email" ? 1 : 2,
        TenantGroupId: getTenantGroupId(),
      };
      let response = await initiateVerification(payload);

      setMultiFactorLevel(response?.secureMultiFactorLevel);
      toSetOtpTarget(contactType);
      setCurrentStep(contactType === "email" ? STEPS.VERIFY_EMAIL : STEPS.VERIFY_PHONE);
      setMessage(`OTP sent to your ${contactType}`);
    } catch (err) {
      setMessage("Failed to send OTP");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinalSignIn = async () => {
    const payload = {
      username: emailId.trim(),
      password: pwd.trim(),
      tenantGroupId: getTenantGroupId(),
    };
    try {
      await signInUser(payload);
    } catch {
      setMessage("Sign-in after MFA failed");
    }
  };

  const handleVerifyOtp = async () => {
    const trimmedOtp = otp.join("").trim();
    if (!trimmedOtp) return setMessage("Enter a valid OTP");

    // For phone or MFA, check 6-digit format
    if ((otpTarget === "phone" || otpTarget === "mfa") && trimmedOtp.length !== 6) {
      return setMessage("Enter a valid 6-digit OTP");
    }
    setIsLoading(true);
    try {
      if (otpTarget === "email") {
        await verifyOtp(userGuid || readCookie("userguid"), trimmedOtp);
      } else if (otpTarget === "phone") {
        await verifyOtp(userGuid || readCookie("userguid"), trimmedOtp);
      } else if (otpTarget === "mfa") {
        await verifyMfa(userGuid || readCookie("userguid"), trimmedOtp);
        setMessage("MFA Verified. Signing you in...");
        await handleFinalSignIn();
      }
      setOtp(Array(6).fill(""));
      toSetOtpTarget("");
      if (otpTarget !== "mfa") setMessage("Verified successfully");
    } catch (err) {
      setMessage("Verification failed");
    } finally {
      setIsLoading(false);
    }
  };
  // Function called on successful email verification
  const onEmailVerified = () => {
    setIsEmailVerified(true);
    setCurrentStep(STEPS.TWO_STEP_SELECT);
  };

  // Function called to start phone verification from TwoStepVerification
  const startPhoneVerification = () => {
    setCurrentStep(STEPS.VERIFY_PHONE);
  };

  // Function called on successful phone verification
  const onPhoneVerified = () => {
    setIsPhoneVerified(true);
    setCurrentStep(STEPS.TWO_STEP_SELECT); // Back to TwoStep, which will now show the CompleteView
  };

  // Function to determine which component to render
  const renderStep = () => {
    if (currentStep === STEPS.GREAT_JOB) {
      return (
        <GreatJob
          isSignSimpli={isSignSimpli}
          toggleModal={callBackSecureSimpliHandler}
          handleVerify={handleInitiateContactVerification}
          isLoading={isLoading}
        />
      );
    }

    if (currentStep === STEPS.VERIFY_EMAIL) {
      return (
        <VerifyByEmail
          toggleModal={callBackSecureSimpliHandler}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          otp={otp}
          toSetOtp={toSetOtp}
          userGuid={userGuid}
          onEmailVerified={onEmailVerified}
          emailId={emailId}
        />
      );
    }

    // TWO_STEP_SELECT is the intermediate view AND the COMPLETE view
    if (currentStep === STEPS.TWO_STEP_SELECT) {
      return (
        <TwoStepVerification
          toggleModal={callBackSecureSimpliHandler}
          isEmailVerified={isEmailVerified}
          isPhoneVerified={isPhoneVerified}
          onVerifyPhone={startPhoneVerification} // Function to move to phone step
          togglePsscVerificationModal={togglePsscVerificationModal}
          multiFactorLevel={multiFactorLevel}
        />
      );
    }

    if (currentStep === STEPS.VERIFY_PHONE) {
      return (
        <VerifyByPhone
          isSignSimpli={isSignSimpli}
          toggleModal={callBackSecureSimpliHandler}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          otp={otp}
          toSetOtp={toSetOtp}
          userGuid={userGuid}
          handleInitiateContactVerification={handleInitiateContactVerification}
          onPhoneVerified={onPhoneVerified}
          onCancel={() => setCurrentStep(STEPS.TWO_STEP_SELECT)} // Option to go back
        />
      );
    }

    // Default or fallback
    return null;
  };
  return (
    <Transition.Root show={psscVerificationModal} as={Fragment}>
      <Dialog as="div" className="relative z-50" static onClose={() => {}} initialFocus={null}>
        {/* Background overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div
                className={`p-[2px] ${
                  currentStep === STEPS.GREAT_JOB ? "bg-rs-border-gradient" : "bg-[#D2E5FF]"
                } rounded-[10px] shadow-xl transition-all w-full ${isSignSimpli ? "max-w-lg" : "max-w-md"}`}
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left ">
                  {/* Loader overlay */}
                  {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10">
                      <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}

                  {renderStep()}
                  {/* {greatJob && (
                  <GreatJob
                    toggleModal={togglePsscVerificationModal}
                    handleVerify={handleVerify}
                    isLoading={isLoading}
                  />
                )}
                {!greatJob && !isEmailVerified && (
                  <VerifyByEmail
                    toggleModal={togglePsscVerificationModal}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                    setIsEmailVerified={setIsEmailVerified}
                  />
                )}
                {(isEmailVerified || isPhoneVerified) && !isPhoneVerificationShown && <TwoStepVerification toggleModal={togglePsscVerificationModal} isEmailVerified={isEmailVerified} isPhoneVerified={isPhoneVerified} setIsPhoneVerificationShown={setIsPhoneVerificationShown} />}
                {isPhoneVerificationShown && <VerifyByPhone 
                    toggleModal={togglePsscVerificationModal}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                    setIsPhoneVerified={setIsPhoneVerified}
                    setIsPhoneVerificationShown={setIsPhoneVerificationShown}/>} */}
                </Dialog.Panel>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
